/**
 * Where a household is up to in a series.
 *
 * ## Why this is a module and not four lines inside the page
 *
 * Two screens ask the question — `SeriesDetail` and `TvSeriesDetail` — and a third
 * answers it already: Continue Watching, on the server, in `progressService`. Three
 * implementations of "which episode are you on" would drift, and the drift would be
 * invisible: the home row would offer S02E05 while the series page marked S02E03, and
 * both would look perfectly reasonable on their own screen.
 *
 * So the rule here is deliberately the same one `continueWatching` uses, and the places
 * it differs are marked and argued rather than accidental.
 *
 * ## The rule
 *
 * Take the episode whose progress was touched most recently — **not** the furthest one.
 * Somebody who watched E01 and E02 and then skipped ahead to E05 has two defensible
 * answers, and only the timestamp says which one they were actually last looking at.
 * If that episode is unfinished, it is the answer. If it is finished, the answer is the
 * next episode in series order, which is what makes finishing S01E10 point at S02E01
 * instead of at nothing.
 */
import type { CatalogItemDto, EpisodeProgressDto } from '@filmodasi/shared';
import { PROGRESS_RESUME_MIN_PERCENT } from '@filmodasi/shared';

export type ProgressMap = Record<string, EpisodeProgressDto>;

/**
 * The episode to continue from, or null.
 *
 * Null means one of two honest things: nobody has started this series, or somebody has
 * finished it. Neither deserves a "Devam et" marker, and inventing one for episode 1
 * would be a lie on the first and a nag on the second — the natural entry point when
 * nothing has been watched is obviously the top of the list, which needs no badge.
 *
 * `episodes` must be in series order — season, then episode — which is the order the
 * server sorts them in. Pass the whole series, not the visible season: a resume point
 * that changed when you looked at another season's tab would be answering a different
 * question from the one being asked.
 */
export function resumeEpisode(
  episodes: CatalogItemDto[],
  progress: ProgressMap,
): CatalogItemDto | null {
  let best: { index: number; episode: CatalogItemDto; completed: boolean } | null = null;
  let bestAt = -Infinity;

  for (const [index, episode] of episodes.entries()) {
    const row = progress[episode.id];
    if (!row) continue;

    /*
     * A position under the resume floor is a mis-click or a preview, and
     * `continueWatching` drops those for exactly that reason. Ignoring it here too is
     * what stops one stray press on the wrong episode from claiming the marker away
     * from the one somebody is genuinely halfway through.
     *
     * Completed rows are exempt: a finished episode is a fact regardless of what
     * `percent` ended up saying, and it is also the seed for the next-episode step.
     */
    if (!row.completed && row.percent < PROGRESS_RESUME_MIN_PERCENT) continue;

    const at = Date.parse(row.updatedAt);
    // An unparseable timestamp is skipped rather than treated as the epoch. Every
    // comparison against NaN is false, so left in it would win or lose depending on
    // iteration order — a silent, position-dependent wrong answer.
    if (!Number.isFinite(at)) continue;

    if (at > bestAt) {
      bestAt = at;
      best = { index, episode, completed: row.completed };
    }
  }

  if (!best) return null;
  if (!best.completed) return best.episode;

  /*
   * Finished, so the answer is the next one along.
   *
   * Where this parts company with `continueWatching`: that function, finding no next
   * episode, moves on to a different *title* — it is building a row of many shows. Here
   * there is nothing to move on to. A household that finished the last episode has
   * finished the series, and falling back to an older row would put "Devam et" on some
   * mid-series episode they watched months ago.
   */
  return episodes[best.index + 1] ?? null;
}

/**
 * The fraction of an episode to draw as a bar, 0–100.
 *
 * Completed reads as full even when `percent` stopped at 92 — the credits threshold is a
 * decision about when something counts as watched, and a bar left visibly short under a
 * "watched" tick would contradict it on screen.
 */
export function watchedPercent(row: EpisodeProgressDto | undefined): number {
  if (!row) return 0;
  if (row.completed) return 100;
  // Clamped rather than trusted: `percent` is stored, and a duration that changed after
  // the fact — a re-scan, a different container — can leave a stale value above 100.
  return Math.max(0, Math.min(100, row.percent));
}
